import { useApolloClient, useMutation } from '@apollo/client';
import { Box, styled } from '@mui/material';
import { FileEarmarkText, Trash } from '@styled-icons/bootstrap';
import useDocumentUploadActions from 'FeatureModules/Document/hooks/useDocumentUploadActions';
import Dropzone, { IDropzoneProps } from 'Components/FileUpload/Dropzone';
import { ActionButton, ErrorMessage, FileNameText } from 'Components/FileUpload/styles';
import useDialog from 'hooks/useDialog';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import { ThemeTypography } from 'DesignSystem';
import { FieldProps, useField, useFormikContext } from 'formik';
import { DELETE_DOCUMENTS } from 'graphql/secure/mutations';
import React, { FC, useEffect, useMemo } from 'react';
import { DropEvent, FileRejection } from 'react-dropzone';
import { Tbooleanish } from 'types/booleanish.types';
import {
  EDatasetProcessingErrorType,
  IFileConfiguration,
  TDatasetProcessingErrorType,
} from 'FeatureModules/DataImport/types/dataset.types';
import UploadItem, { UploadItemContainer } from './UploadItem';
import { IBaseDocument, TDocumentCategory } from 'FeatureModules/Document/types/document.types';
import { DOCUMENT_FRAGMENT } from 'graphql/secure/fragments/document.fragments';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import FlexBox from 'Components/FlexBox';
import FileProcessingConfig from '../FileProcessingConfig';

interface IUploadFieldProps {
  multiple?: boolean;
  disabled?: boolean;
  disableErrorMessage?: boolean;
  showFileConfiguration?: boolean;
  supportedFileTypes?: IDropzoneProps['supportedFileTypes'];
  fileConfiguration: IFileConfiguration;
  /**
   * Default document category to assign to uploaded documents
   * @default 'GEOGRAPHICAL_FEATURES'
   */
  defaultDocumentCategory?: TDocumentCategory;
  allowDocumentEditing?: boolean;
  onFileConfigurationChange: (fileConfiguration: IFileConfiguration) => void;
}

interface IUploadFile {
  id: string;
  title: string;
  inUploadContext: boolean;
  processingError?: { type?: TDatasetProcessingErrorType; message: string };
}

const Background = styled(Box)<{ error?: Tbooleanish }>(({ theme, error }) => ({
  background:
    error === 'true' ? theme.custom.themeColors.error[20] : theme.custom.themeColors.primary[5],
  borderRadius: 4,
  padding: theme.spacing(1),
}));

/**
 * Need to access the UploadContext so wrap the UploadField in the UploadProvider
 */
const UploadField: FC<IUploadFieldProps & FieldProps<IUploadFile[]>> = ({
  supportedFileTypes,
  multiple,
  disabled,
  disableErrorMessage,
  defaultDocumentCategory = 'GEOGRAPHICAL_FEATURES',
  showFileConfiguration,
  allowDocumentEditing,
  fileConfiguration = { decimalSeparator: 'DOT', csvSeparator: 'AUTO_DETECT' },
  field: { name, value },
  onFileConfigurationChange,
}) => {
  const { setStatus } = useFormikContext();
  const [, { touched }, { setValue, setError, setTouched }] = useField<IUploadFile[] | undefined>(
    name
  );
  const client = useApolloClient();
  const { fileIDs, fileMap } = useUploadState();
  const { onDrop, maxSize } = useDocumentUploadActions();
  const { openDialog } = useDialog();
  const [_deleteDocument] = useMutation<{ deleteDocument: { ids: string } }, { ids: string[] }>(
    DELETE_DOCUMENTS
  );

  // check if there are any files with the .csv extension
  const hasCsvFiles = value.some(({ title }) => title.endsWith('.csv'));

  useEffect(() => {
    if (fileIDs.length) {
      const datasetRecordIds = fileIDs
        .filter(id => fileMap[id].contextObject.formikName === name && fileMap[id].recordId)
        .map(id => ({
          id: fileMap[id].recordId,
          uploaded: fileMap[id].progress === 100,
          title: fileMap[id].data.name,
          inUploadContext: true,
        }));
      setStatus({ uploadCompleted: datasetRecordIds.every(record => record.uploaded) });
      setValue(
        [
          ...value.filter(
            ({ id }) => datasetRecordIds.findIndex(record => id === record.id) === -1
          ),
          ...datasetRecordIds,
        ],
        false
      );
      let index: number;
      if ((index = fileIDs.findIndex(id => fileMap[id].error)) !== -1) {
        setError(fileMap[fileIDs[index]].error);
      } else {
        setError(undefined);
      }
    } else {
      setStatus({ uploadCompleted: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, fileIDs, fileMap]);

  const contextFileIds = useMemo(
    () =>
      value
        .filter(({ inUploadContext }) => inUploadContext)
        .map(file => fileIDs.find(id => file.id === fileMap[id].recordId))
        .filter(id => id !== undefined) as string[],
    [value, fileIDs, fileMap]
  );

  const nonContextFiles = useMemo(
    () => value.filter(({ inUploadContext }) => !inUploadContext),
    [value]
  );

  const nonContextDocuments = useMemo(
    () =>
      nonContextFiles.map(file => {
        if (file.processingError) {
          return file;
        }
        const cachedDocument: IBaseDocument | null = client.readFragment({
          id: `Document:${file.id}`,
          fragment: DOCUMENT_FRAGMENT,
          fragmentName: 'documentValues',
        });
        return cachedDocument ?? file;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonContextFiles]
  );

  useEffect(() => {
    if (contextFileIds.length) {
      setTouched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextFileIds]);

  const hasFilesAttached = value.length > 0;
  const fileUploadErrors = contextFileIds.filter(id => !!fileMap[id].error);
  const fileParsingErrorCount = value.filter(({ processingError }) => processingError).length;
  const hasFileGeoJsonNotClosingError = value?.some(
    ({ processingError }) =>
      processingError?.type === EDatasetProcessingErrorType.OPEN_GEOJSON_GEOMETRY
  );

  const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
    // Add formikName to the file object so we can track which field the file belongs to
    onDrop(acceptedFiles, fileRejections, {
      formikName: name,
      category: defaultDocumentCategory,
    });
  };

  const handleDeleteDocument = (documentId: string) => {
    setTouched(true);
    setValue(value?.filter(file => file.id !== documentId) || [], true); // delete the file from the formik state
    _deleteDocument?.({ variables: { ids: [documentId] } });
  };

  return (
    <Box>
      {!disableErrorMessage && !hasFilesAttached && touched && (
        <Background error="true" mb={2}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="RED">
            Upload at least one file
          </ThemeTypography>
        </Background>
      )}
      {!disableErrorMessage && fileParsingErrorCount > 0 && (
        <Background error="true" mb={2}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="RED">
            Note and address the errors here in order to include all your geo-data for your due
            diligence.
          </ThemeTypography>
        </Background>
      )}

      {/* Hide the dropzone if its single file upload and there is already an uploaded file */}
      {!disabled && (multiple || (!multiple && !hasFilesAttached)) && (
        <Dropzone
          supportedFileTypes={supportedFileTypes}
          disabled={disabled}
          maxFiles={500}
          maxFileMb={maxSize}
          multiple={multiple}
          onDrop={handleDrop}
        />
      )}

      {hasFilesAttached && showFileConfiguration && (
        <Box mb={2}>
          <FileProcessingConfig
            fileConfiguration={fileConfiguration}
            displayCsvSeparator={hasCsvFiles}
            displayAutoClosingGeometriesSelection={hasFileGeoJsonNotClosingError}
            onFileConfigurationChange={onFileConfigurationChange}
          />
        </Box>
      )}

      <FlexBox mb={2} gap={2}>
        {hasFilesAttached && (
          <Background>
            <ThemeTypography variant="BODY_MEDIUM_BOLD">
              {value.length} {value.length > 1 ? 'files' : 'file'}
            </ThemeTypography>
          </Background>
        )}
        {!!fileUploadErrors.length && (
          <Background error="true">
            <ThemeTypography variant="BODY_MEDIUM_BOLD" color="RED">
              {fileUploadErrors.length} upload errors
            </ThemeTypography>
          </Background>
        )}
        {fileParsingErrorCount > 0 && (
          <Background error="true">
            <ThemeTypography variant="BODY_MEDIUM_BOLD" color="RED">
              {fileParsingErrorCount} parsing errors
            </ThemeTypography>
          </Background>
        )}
      </FlexBox>

      <Box maxHeight={1000} overflow="auto">
        {contextFileIds?.map(id => {
          return (
            <UploadItem
              key={id}
              id={id}
              allowDocumentEditing={allowDocumentEditing}
              processingError={
                value?.find(file => fileMap[id].recordId === file.id)?.processingError
              }
              onFileDelete={handleDeleteDocument}
            />
          );
        })}

        {nonContextDocuments.map(document => (
          <UploadItemContainer key={document.id} data-testid="uploaded-document-item">
            {'__typename' in document ? (
              // document is IBaseDocument (from Apollo cache)
              <DocumentIntroTitle document={document as IBaseDocument} />
            ) : (
              // document is IUploadFile (local upload context)
              <Box display="flex" alignItems="center" width="90%">
                <FileEarmarkText size={28} />
                <Box display="flex" flexGrow={1} flexDirection="column" width="70%" gap={0.5}>
                  <Box>
                    <FileNameText>{document.title}</FileNameText>
                  </Box>
                  {'processingError' in document && document.processingError && (
                    <ErrorMessage error>{document.processingError.message}</ErrorMessage>
                  )}
                </Box>
              </Box>
            )}
            <FlexBox gap={1}>
              {allowDocumentEditing && '__typename' in document && (
                <ActionButton
                  onClick={() => {
                    if ('__typename' in document) {
                      openDialog({
                        type: 'EDIT_DOCUMENT',
                        props: {
                          document: document as IBaseDocument,
                          onDelete: () => handleDeleteDocument(document.id),
                        },
                      });
                    }
                  }}
                >
                  <Icon name="edit" />
                </ActionButton>
              )}
              <ActionButton
                onClick={() => handleDeleteDocument(document.id)}
                data-testid="delete-uploaded-document"
              >
                <Trash size={14} />
              </ActionButton>
            </FlexBox>
          </UploadItemContainer>
        ))}
      </Box>
    </Box>
  );
};

export default UploadField;
